import { useEffect, useState } from "react";
import { Award, Crown, Trophy, X } from "lucide-react";

import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import {
    FLOW_SHORT_KEY,
    TROPHY_DEPARTMENTS,
    daysSince,
    formatPoints,
    type TrophyFlow,
} from "../gamification/trophies";
import "../../styles/points-admin.css";

// Floor order for the records table: parent department, then window.
const FLOW_ORDER = new Map(
    TROPHY_DEPARTMENTS.flatMap((d, i) => d.flows.map((f, j) => [f as string, i * 10 + j])),
);

// Which parent department a flow belongs to, for "Sorting · Sorter 1" labels.
const FLOW_DEPT = new Map(
    TROPHY_DEPARTMENTS.flatMap(d => d.flows.map(f => [f as string, d])),
);

interface UserTrophies {
    uid: number;
    name: string | null;
    warehouse: string | null;
    lifetime: number;
    bestDay: number;
    streakDays: number;
    milestones: { code: string; value: number; achievedAt: string }[];
    records: { flow: string; windowMinutes: number; points: number; items: number; setAt: string }[];
}

interface Props {
    uid: number;
    /** Fallback while the fetch is in flight — the row's name. */
    name: string | null;
    onClose: () => void;
}

const MILESTONE_SECTIONS: { prefix: string; labelKey: string }[] = [
    { prefix: "lifetime_", labelKey: "trophies_section_lifetime" },
    { prefix: "day_", labelKey: "trophies_section_day" },
    { prefix: "golden_hour_", labelKey: "trophies_section_golden_hour" },
    { prefix: "streak_", labelKey: "trophies_section_streak" },
];

const fmtValue = (v: number) =>
    v >= 1_000_000 ? `${v / 1_000_000}M` : v >= 1000 ? `${v / 1000}k` : `${v}`;

/**
 * Admin leaderboard click-through: one operator's trophy cabinet — totals,
 * the worldwide crowns they hold, and every milestone rung they've crossed.
 * Read-only; the operator's own celebration lives on the handheld.
 */
const UserTrophiesModal = ({ uid, name, onClose }: Props) => {
    const { t } = useTranslation();
    const [data, setData] = useState<UserTrophies | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/admin/points/user-trophies?uid=${uid}`, {});
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const body = await res.json();
                if (alive) setData(body);
            } catch {
                if (alive) setFailed(true);
            }
        })();
        return () => { alive = false; };
    }, [uid]);

    const windowLabel = (w: number) =>
        w < 60 ? `${w} ${t("trophies_min")}` : `${w / 60} ${t("trophies_hr")}`;
    const fmtDate = (iso: string) => {
        try {
            return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
        } catch {
            return "—";
        }
    };

    return (
        <div className="points-trophy-overlay" onClick={onClose} data-testid="user-trophies-overlay">
            <div className="points-trophy-modal" onClick={e => e.stopPropagation()}>
                <div className="points-trophy-modal-head">
                    <Trophy size={20} color="#f59e0b" />
                    <div>
                        <div style={{ fontSize: "1.05rem", fontWeight: 900, lineHeight: 1.1 }}>
                            {data?.name ?? name ?? `#${uid}`}
                        </div>
                        <div style={{ fontSize: "0.74rem", fontWeight: 600, color: "var(--color-text-muted, #6b7280)" }}>
                            {data?.warehouse ?? ""}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-ghost dispatch-modal-btn"
                        style={{ marginLeft: "auto" }}
                        onClick={onClose}
                        aria-label={t("trophies_close")}
                    >
                        <X size={14} />
                    </button>
                </div>

                {failed ? (
                    <div className="points-trophy-empty">{t("server_error")}</div>
                ) : !data ? (
                    <div className="points-trophy-empty">{t("loading")}</div>
                ) : (
                    <>
                        <div className="points-trophy-stats">
                            <div className="points-trophy-stat">
                                <span className="points-trophy-stat-value">{formatPoints(data.lifetime)}</span>
                                <span className="points-trophy-stat-label">{t("trophies_lifetime")}</span>
                            </div>
                            <div className="points-trophy-stat">
                                <span className="points-trophy-stat-value">{formatPoints(data.bestDay)}</span>
                                <span className="points-trophy-stat-label">{t("trophies_best_day")}</span>
                            </div>
                            <div className="points-trophy-stat">
                                <span className="points-trophy-stat-value">{data.streakDays}</span>
                                <span className="points-trophy-stat-label">{t("trophies_streak")}</span>
                            </div>
                            <div className="points-trophy-stat">
                                <span className="points-trophy-stat-value">{data.records.length}</span>
                                <span className="points-trophy-stat-label">{t("points_tab_records")}</span>
                            </div>
                        </div>

                        <div className="points-trophy-section">{t("trophies_worldwide")}</div>
                        {data.records.length === 0 ? (
                            <div className="points-trophy-empty">{t("points_user_no_records")}</div>
                        ) : (
                            <div className="locations-table-wrap">
                                <table className="locations-table points-records-table">
                                    <thead>
                                        <tr>
                                            <th>{t("points_col_flow")}</th>
                                            <th>{t("points_col_window")}</th>
                                            <th>{t("trophies_items")}</th>
                                            <th>{t("points_col_held")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...data.records]
                                            .sort((a, b) =>
                                                (FLOW_ORDER.get(a.flow) ?? 99) - (FLOW_ORDER.get(b.flow) ?? 99)
                                                || a.windowMinutes - b.windowMinutes)
                                            .map(r => {
                                                const dept = FLOW_DEPT.get(r.flow);
                                                const label = dept && dept.flows.length > 1
                                                    ? `${t(`trophies_dept_${dept.key}`)} · ${t(FLOW_SHORT_KEY[r.flow as TrophyFlow])}`
                                                    : t(`trophies_flow_${r.flow}`);
                                                return (
                                                    <tr key={`${r.flow}-${r.windowMinutes}`}>
                                                        <td>
                                                            <Crown size={12} className="points-rank-icon points-rank-1" />
                                                            {" "}{label}
                                                        </td>
                                                        <td>{windowLabel(r.windowMinutes)}</td>
                                                        <td><span className="points-score-value">{formatPoints(r.items)}</span></td>
                                                        <td>{daysSince(r.setAt)}{t("trophies_days_short")}</td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {MILESTONE_SECTIONS.map(({ prefix, labelKey }) => {
                            const rungs = data.milestones.filter(m => m.code.startsWith(prefix));
                            if (rungs.length === 0) return null;
                            return (
                                <div key={prefix}>
                                    <div className="points-trophy-section">{t(labelKey)}</div>
                                    <div className="points-trophy-chips">
                                        {rungs.map(m => (
                                            <span key={m.code} className="points-trophy-chip">
                                                <Award size={12} color="#f59e0b" />
                                                {fmtValue(m.value)}
                                                <span style={{ fontWeight: 600, color: "var(--ink-400, #8694A2)", fontSize: "0.66rem" }}>
                                                    {fmtDate(m.achievedAt)}
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                        {data.milestones.length === 0 && (
                            <>
                                <div className="points-trophy-section">{t("trophies_tab_milestones")}</div>
                                <div className="points-trophy-empty">{t("points_user_no_milestones")}</div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default UserTrophiesModal;
